import { PlusOutlined, WarningFilled } from "@ant-design/icons";
import { Button, Checkbox, Form, Select, Table } from "antd";
import { FullHeightContainerLayout } from 'components';
import moment from 'moment';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Column } = Table;
const { Option } = Select;

const RoleUserList = ({ formRef, userList, users, onUserChange, readOnly, onDeleteUser, getFormErrors, add, remove }) => {
    const [scrollToLastRow, setScrollToLastRow] = useState(false);
    
    // Helper function to check if role assignment is active
    const isRoleAssignmentActive = (field) => {
        return formRef.current?.getFieldValue(["users", field.name, "active"]) !== false;
    };
    
    // Helper function to get user account status from userList
    const getUserAccountStatus = (userId) => {
        const user = userList.find(u => u.userId === userId);
        return user ? user.active : true;
    };
    
    // Helper function to sync role assignment with user account status
    const syncRoleAssignmentWithUserStatus = (userId, field) => {
        const userAccountActive = getUserAccountStatus(userId);
        // Auto-sync: if user account is inactive, role assignment should also be inactive
        // if user account is active, role assignment can be managed independently
        if (!userAccountActive) {
            formRef.current.setFieldsValue({
                users: {
                    [field.name]: {
                        ...formRef.current.getFieldValue(["users", field.name]),
                        active: false
                    }
                }
            });
        }
    };
    
    return (
        <FullHeightContainerLayout
            showHeader={false}
            showFooter={true}
            content={
                <Table size="small"
                    dataSource={users}
                    pagination={false}
                    scroll={{ y: "100vh" }}
                    className="form-table container-height-100"
                    data-testid={CypressTestIds.ROLES_USER_LIST_TABLE}
                    rowClassName={(record, index) => {
                        const isActive = isRoleAssignmentActive(record);
                        return isActive ? '' : 'user-inactive-row';
                    }}>
                    <Column
                        width="5%"
                        title="No."
                        key="index"
                        render={(value, field, index) => index + 1}
                    />
                    <Column
                        title={"User"}
                        render={(value, field, index) => {
                            const isRoleActive = isRoleAssignmentActive(field);
                            const currentUserId = formRef.current?.getFieldValue(["users", field.name, "userId"]);
                            const isUserAccountActive = getUserAccountStatus(currentUserId);
                            
                            return (
                                <Form.Item
                                    {...field}
                                    name={[field.name, "userId"]}
                                    {...getFormErrors(`users.${field.name}.userId`)}>
                                    <Select
                                        showSearch
                                        placeholder="Search to Select"
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                        onChange={(value) => {
                                            onUserChange(value, field);
                                            // Auto-sync role assignment status with user account status
                                            Promise.resolve().then(() => {
                                                syncRoleAssignmentWithUserStatus(value, field);
                                            });
                                        }}
                                        disabled={readOnly} // Only disabled when readOnly, NOT when inactive
                                        style={!isRoleActive ? { opacity: 0.7 } : {}}
                                        {...(() => (readOnly ? { open: false } : {}))()}
                                        data-testid={CypressTestIds.ROLES_USER_LIST_USER_ID_SELECT}>
                                        {[...userList]
                                            .sort((a, b) => (a.email || "").localeCompare(b.email || "", undefined, { sensitivity: "base" }))
                                            .map(user => (
                                            <Option key={user.userId} value={user.userId}>
                                                <span style={!user.active ? { color: '#ff6b35', fontStyle: 'italic' } : {}}>
                                                    {user.email}
                                                    {!user.active && (
                                                        <WarningFilled style={{ color: '#ff6b35', marginLeft: '6px' }} title="User account is inactive" />
                                                    )}
                                                </span>
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            );
                        }}
                    />
                    <Column
                        title={"Name"}
                        render={(value, field, index) => {
                            const isRoleActive = isRoleAssignmentActive(field);
                            const currentUserId = formRef.current?.getFieldValue(["users", field.name, "userId"]);
                            const isUserAccountActive = getUserAccountStatus(currentUserId);
                            
                            return (
                                <Form.Item {...field}>
                                   <div style={!isRoleActive ? { opacity: 0.6, color: '#999' } : {}}>
                                        {formRef.current.getFieldValue(["users", field.name, "firstName"])} {formRef.current.getFieldValue(["users", field.name, "lastName"])}
                                        {!isUserAccountActive && (
                                            <span style={{ 
                                                fontStyle: 'italic', 
                                                marginLeft: '8px', 
                                                color: '#ff6b35',
                                                fontSize: '11px',
                                                fontWeight: 'bold'
                                            }}>
                                                (USER ACCOUNT INACTIVE)
                                            </span>
                                        )}
                                    </div>
                                </Form.Item>
                            );
                        }}
                    />
                    <Column
                        width="15%"
                        title={"Date Assigned"}
                        render={(value, field, index) => {
                            const isActive = isRoleAssignmentActive(field);
                            return (
                                <Form.Item
                                    {...field}
                                    name={[field.name, "assignedDate"]}>
                                    <div style={!isActive ? { opacity: 0.6, color: '#999' } : {}}>
                                        {moment.utc(formRef.current.getFieldValue(["users", field.name, "assignedDate"])).toDate().toLocaleString()}
                                    </div>
                                </Form.Item>
                            );
                        }}
                    />
                    <Column
                        width="10%"
                        title={"Active"}
                        render={(value, field, index) => {
                            const currentUserId = formRef.current?.getFieldValue(["users", field.name, "userId"]);
                            const isUserAccountActive = getUserAccountStatus(currentUserId);
                            
                            return (
                                <Form.Item
                                    {...field}
                                    name={[field.name, "active"]}
                                    valuePropName="checked"
                                    initialValue={true}>
                                    <Checkbox 
                                        disabled={readOnly || !isUserAccountActive} // Disabled if readOnly OR if user account is inactive
                                        data-testid={CypressTestIds.ROLES_USER_LIST_USER_ACTIVE_CHECK}>
                                    </Checkbox>
                                    {!isUserAccountActive && (
                                        <div style={{ 
                                            fontSize: '10px', 
                                            color: '#ff6b35', 
                                            marginTop: '2px',
                                            fontStyle: 'italic'
                                        }}>
                                            Locked (User Inactive)
                                        </div>
                                    )}
                                </Form.Item>
                            );
                        }}
                    />
                    <Column
                        width="5%"
                        render={(value, field, index) => {
                            const isActive = isRoleAssignmentActive(field);
                            return (
                                <FontAwesomeIcon
                                    icon={faTrashCan}
                                    className={!readOnly ? (isActive ? "action-cursor text-blue-500" : "action-cursor text-gray-400 opacity-50") : "text-gray-400 opacity-45"}
                                    onClick={() => {
                                        if (!readOnly) onDeleteUser(field, () => remove(field.name));
                                    }}
                                    data-testid={CypressTestIds.ROLES_USER_LIST_USER_DELETE_BUTTON}/>
                            );
                        }}
                    />
                </Table>
            }
            footer={
                <Form.Item>
                    <Button
                        block
                        onClick={() => {
                            setScrollToLastRow(true);
                            add();
                        }}
                        data-testid={CypressTestIds.ROLES_USER_LIST_ADD_USER_BUTTON}
                        disabled={readOnly}>
                        <PlusOutlined />
                        {users.length === 0 ? "Add user" : "Add another user"}
                    </Button>
                </Form.Item>
            }>
        </FullHeightContainerLayout>
    );
}

export default RoleUserList;